import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Tags, Plus, Save, Trash2, Loader2, Search, Calculator, Percent, X, Receipt, Pencil } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'

export default function TabelasPrecoPage() {
  const qc = useQueryClient()
  const [sel, setSel] = useState<number | null>(null)
  const [novoNome, setNovoNome] = useState('')

  const { data: tabelas = [] } = useQuery<any[]>({
    queryKey: ['tabelas-preco'],
    queryFn: async () => (await api.get('/comercial/tabelas-preco')).data,
  })

  const criar = useMutation({
    mutationFn: async () => (await api.post('/comercial/tabelas-preco', { nome: novoNome })).data,
    onSuccess: (d: any) => { toast.success('Tabela criada'); setNovoNome(''); qc.invalidateQueries({ queryKey: ['tabelas-preco'] }); setSel(d.id) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const excluir = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/comercial/tabelas-preco/${id}`)).data,
    onSuccess: () => { toast.success('Tabela excluída'); setSel(null); qc.invalidateQueries({ queryKey: ['tabelas-preco'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const renomear = useMutation({
    mutationFn: async ({ id, nome, descricao, ativo }: any) =>
      (await api.put(`/comercial/tabelas-preco/${id}`, { nome, descricao: descricao ?? null, ativo: ativo ?? true })).data,
    onSuccess: () => { toast.success('Tabela renomeada'); qc.invalidateQueries({ queryKey: ['tabelas-preco'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-xl font-bold flex items-center gap-2"><Tags size={22} /> Tabelas de Preço</h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Lista de tabelas */}
        <div className="card card-body space-y-2">
          <div className="text-xs font-semibold text-ink-500 uppercase">Tabelas</div>
          {tabelas.map((t) => (
            <div key={t.id} className={`flex items-center justify-between rounded px-2 py-1.5 cursor-pointer ${sel === t.id ? 'bg-brand-100 text-brand-800' : 'hover:bg-ink-50'}`}
              onClick={() => setSel(t.id)}>
              <span className="text-sm">{t.nome} <span className="text-xs text-ink-400">({t.qtd_itens})</span></span>
              <div className="flex items-center gap-2">
                <button onClick={(e) => { e.stopPropagation(); const n = prompt('Novo nome da tabela:', t.nome); if (n && n.trim() && n.trim() !== t.nome) renomear.mutate({ id: t.id, nome: n.trim(), descricao: t.descricao, ativo: t.ativo }) }} className="text-ink-500 hover:text-brand-600" title="Renomear"><Pencil size={13} /></button>
                <button onClick={(e) => { e.stopPropagation(); pedirExclusao({ descricao: `a tabela "${t.nome}"`, onConfirm: () => excluir.mutate(t.id) }) }} className="text-red-500" title="Excluir"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          <div className="flex gap-1 pt-2 border-t">
            <input value={novoNome} onChange={(e) => setNovoNome(e.target.value)} placeholder="Nova tabela…" className="input !py-1 text-sm flex-1" />
            <button onClick={() => novoNome && criar.mutate()} className="px-2 rounded bg-brand-600 text-white"><Plus size={16} /></button>
          </div>
        </div>

        {/* Editor de preços */}
        <div className="md:col-span-3">
          {sel ? <EditorTabela tabela={tabelas.find((t) => t.id === sel)} tabelaId={sel} /> : <div className="card card-body text-center text-ink-500">Selecione ou crie uma tabela.</div>}
        </div>
      </div>
    </div>
  )
}

function EditorTabela({ tabela, tabelaId }: { tabela: any; tabelaId: number }) {
  const qc = useQueryClient()
  const [busca, setBusca] = useState('')
  const [precos, setPrecos] = useState<Record<number, string>>({})
  const [stOn, setStOn] = useState(false)
  const [stPct, setStPct] = useState('')
  const [descAvista, setDescAvista] = useState('')
  const [reajusteOpen, setReajusteOpen] = useState(false)

  // sincroniza config de ST/desconto com a tabela selecionada
  useEffect(() => {
    setStOn(!!tabela?.acrescenta_st)
    setStPct(tabela?.st_percentual != null ? String(tabela.st_percentual) : '')
    setDescAvista(tabela?.desconto_avista_pct != null ? String(tabela.desconto_avista_pct) : '')
  }, [tabelaId, tabela?.acrescenta_st, tabela?.st_percentual, tabela?.desconto_avista_pct])

  const salvarConfig = useMutation({
    mutationFn: async () => (await api.put(`/comercial/tabelas-preco/${tabelaId}`, {
      nome: tabela?.nome, descricao: tabela?.descricao ?? null, ativo: tabela?.ativo ?? true,
      acrescenta_st: stOn, st_percentual: stOn && stPct !== '' ? Number(stPct) : null,
      desconto_avista_pct: descAvista !== '' ? Number(descAvista) : 0,
    })).data,
    onSuccess: () => { toast.success('Configuração salva'); qc.invalidateQueries({ queryKey: ['tabelas-preco'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const { data: produtos = [] } = useQuery<any[]>({
    queryKey: ['produtos-tabela'],
    queryFn: async () => (await api.get('/cadastros/produtos', { params: { limit: 1000, finalidade: 'revenda' } })).data,
  })
  const { data: itens = [] } = useQuery<any[]>({
    queryKey: ['tabela-itens', tabelaId],
    queryFn: async () => (await api.get(`/comercial/tabelas-preco/${tabelaId}/itens`)).data,
  })

  // inicializa precos com os itens já salvos
  useEffect(() => {
    const m: Record<number, string> = {}
    for (const it of itens) m[it.produto_id] = String(it.preco)
    setPrecos(m)
  }, [itens, tabelaId])

  const salvar = useMutation({
    mutationFn: async () => {
      const lista = Object.entries(precos)
        .filter(([, v]) => v !== '' && !isNaN(Number(v)))
        .map(([pid, v]) => ({ produto_id: Number(pid), preco: Number(v) }))
      return (await api.put(`/comercial/tabelas-preco/${tabelaId}/itens`, { itens: lista })).data
    },
    onSuccess: (d: any) => toast.success(`${d.salvos} preço(s) salvos`),
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const filtrados = useMemo(() => {
    const q = busca.toLowerCase()
    return (produtos || []).filter((p) => !q || (p.descricao || '').toLowerCase().includes(q) || (p.codigo || '').toLowerCase().includes(q))
  }, [produtos, busca])

  return (
    <div className="card card-body space-y-3">
      {/* Config da tabela: reajuste em massa + ST */}
      <div className="rounded-lg border border-ink-200 bg-ink-50/60 p-3 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="font-semibold text-ink-700">{tabela?.nome}</div>
          <button onClick={() => setReajusteOpen(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium">
            <Calculator size={15} /> Reajustar preços
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-3 border-t border-ink-200 pt-3">
          <label className="flex items-center gap-2 text-sm font-medium text-ink-700 cursor-pointer">
            <input type="checkbox" checked={stOn} onChange={(e) => setStOn(e.target.checked)} />
            <Receipt size={15} className="text-amber-600" /> Acrescentar ST (pessoa física / consumidor)
          </label>
          {stOn && (
            <div className="flex items-center gap-1">
              <input type="number" step="0.01" min="0" value={stPct} onChange={(e) => setStPct(e.target.value)}
                placeholder="0,00" className="input !py-1 text-sm text-right w-24" />
              <Percent size={14} className="text-ink-400" />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm font-medium text-ink-700 border-l border-ink-200 pl-3">
            <Percent size={15} className="text-emerald-600" /> Desconto à vista (teto p/ vendedor)
            <input type="number" step="0.01" min="0" max="100" value={descAvista} onChange={(e) => setDescAvista(e.target.value)}
              placeholder="0,00" className="input !py-1 text-sm text-right w-24" />
            <span className="text-ink-400 text-xs">%</span>
          </label>
          <button onClick={() => salvarConfig.mutate()} disabled={salvarConfig.isPending}
            className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-lg border border-ink-300 hover:bg-ink-100 text-sm font-medium disabled:opacity-50">
            {salvarConfig.isPending ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Salvar
          </button>
        </div>
        {stOn && (
          <p className="text-xs text-ink-500">
            Os preços desta tabela saem acrescidos de <b>{stPct || 0}%</b> de ST na venda (ex.: {formatMoney(100)} → {formatMoney(100 * (1 + (Number(stPct) || 0) / 100))}).
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-2 top-2.5 text-ink-400" />
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar produto…" className="input !pl-8 !py-1.5 text-sm w-full" />
        </div>
        <button onClick={() => salvar.mutate()} disabled={salvar.isPending}
          className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
          {salvar.isPending ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Salvar preços
        </button>
      </div>
      <div className="table-wrap max-h-[65vh] overflow-auto">
        <table className="table text-sm">
          <thead className="sticky top-0 bg-white">
            <tr><th>Código</th><th>Produto</th><th className="text-right">Preço padrão</th><th className="text-right w-40">Preço nesta tabela</th></tr>
          </thead>
          <tbody>
            {filtrados.map((p) => (
              <tr key={p.id}>
                <td className="text-ink-500">{p.codigo}</td>
                <td>{p.descricao}</td>
                <td className="text-right text-ink-400">{formatMoney(p.preco_venda || 0)}</td>
                <td className="text-right">
                  <input type="number" step="0.01" value={precos[p.id] ?? ''}
                    onChange={(e) => setPrecos((s) => ({ ...s, [p.id]: e.target.value }))}
                    placeholder={String(p.preco_venda || 0)}
                    className="input !py-1 text-sm text-right w-32" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-ink-500">Produtos sem preço preenchido usam o preço padrão do produto na venda.</p>

      {reajusteOpen && (
        <ReajusteModal tabelaId={tabelaId} qtd={itens.length}
          onClose={() => setReajusteOpen(false)}
          onDone={() => {
            setReajusteOpen(false)
            qc.invalidateQueries({ queryKey: ['tabela-itens', tabelaId] })
            qc.invalidateQueries({ queryKey: ['tabelas-preco'] })
          }} />
      )}
    </div>
  )
}

function ReajusteModal({ tabelaId, qtd, onClose, onDone }: { tabelaId: number; qtd: number; onClose: () => void; onDone: () => void }) {
  const [tipo, setTipo] = useState<'percentual' | 'valor'>('percentual')
  const [operacao, setOperacao] = useState<'aumentar' | 'diminuir'>('aumentar')
  const [valor, setValor] = useState('')

  const aplicar = useMutation({
    mutationFn: async () => (await api.post(`/comercial/tabelas-preco/${tabelaId}/reajustar-itens`,
      { tipo, operacao, valor: Number(valor) })).data,
    onSuccess: (d: any) => { toast.success(`${d.ajustados} preço(s) reajustados`); onDone() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro no reajuste'),
  })

  const exemplo = (() => {
    const v = Number(valor) || 0
    const sinal = operacao === 'aumentar' ? 1 : -1
    const novo = tipo === 'percentual' ? 100 * (1 + sinal * v / 100) : 100 + sinal * v
    return Math.max(0, novo)
  })()

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold flex items-center gap-2"><Calculator size={18} /> Reajustar preços</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-ink-500">Aplica a <b>todos os {qtd} itens</b> com preço nesta tabela.</p>

          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setOperacao('aumentar')}
              className={`h-11 rounded-lg font-semibold text-sm border-2 ${operacao === 'aumentar' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-ink-200 text-ink-500'}`}>↑ Aumentar</button>
            <button onClick={() => setOperacao('diminuir')}
              className={`h-11 rounded-lg font-semibold text-sm border-2 ${operacao === 'diminuir' ? 'border-red-500 bg-red-50 text-red-700' : 'border-ink-200 text-ink-500'}`}>↓ Diminuir</button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setTipo('percentual')}
              className={`h-11 rounded-lg font-semibold text-sm border-2 ${tipo === 'percentual' ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-ink-200 text-ink-500'}`}>Percentual (%)</button>
            <button onClick={() => setTipo('valor')}
              className={`h-11 rounded-lg font-semibold text-sm border-2 ${tipo === 'valor' ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-ink-200 text-ink-500'}`}>Valor (R$)</button>
          </div>

          <div>
            <label className="text-xs font-medium text-ink-500">{tipo === 'percentual' ? 'Percentual a aplicar' : 'Valor em R$'}</label>
            <input type="number" step="0.01" min="0" value={valor} onChange={(e) => setValor(e.target.value)} autoFocus
              placeholder={tipo === 'percentual' ? 'ex.: 10' : 'ex.: 2,50'} className="input w-full mt-1 text-lg" />
          </div>

          {valor !== '' && (
            <div className="text-sm bg-ink-50 rounded-lg p-3 text-ink-600">
              Exemplo: um item de <b>{formatMoney(100)}</b> vira <b>{formatMoney(exemplo)}</b>.
            </div>
          )}

          <button onClick={() => aplicar.mutate()} disabled={aplicar.isPending || valor === '' || Number(valor) <= 0}
            className="w-full h-12 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50">
            {aplicar.isPending ? <Loader2 className="animate-spin" size={18} /> : <Calculator size={18} />} Aplicar reajuste
          </button>
        </div>
      </div>
    </div>
  )
}
